import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Loader2, Search, X, ArrowUpDown, ArrowUp, ArrowDown, CalendarIcon, Copy, Check, Clock, Coins } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useTestClock } from '@/contexts/TestClockContext';
import { cn } from '@/lib/utils';
import { safeLog } from '@/utils/safeLogger';

interface Purchase {
  id: string;
  user_email: string;
  type: 'subscription' | 'topup';
  details: string;
  credits: number;
  amount: number | null;
  status: string;
  created_at: string;
  stripe_payment_id?: string | null;
  credits_before?: number | null;
  credits_after?: number | null;
}

export function CreditPurchasesLog() {
  const { isTestMode, getCurrentTime } = useTestClock();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading Credit Purchases...');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'subscription' | 'topup'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  
  // Sort State
  const [sortField, setSortField] = useState<keyof Purchase>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadPurchases();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('credit-purchases-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions'
        },
        (payload) => {
          safeLog.info('[CreditPurchasesLog] Subscription change detected', { event: payload.eventType });
          loadPurchases();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'credit_topups'
        },
        (payload) => {
          safeLog.info('[CreditPurchasesLog] Top-up change detected', { event: payload.eventType });
          loadPurchases();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  async function loadPurchases() {
    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('get-credit-purchases');
      
      if (error) throw error;
      if (!data?.purchases) throw new Error('No data returned');

      setTotalCount(data.purchases.length);
      setPurchases(data.purchases);
      safeLog.info('[CreditPurchasesLog] Loaded purchases', { count: data.purchases.length });
    } catch (error) {
      safeLog.error('Error loading purchases', error as Error);
    } finally {
      setLoading(false);
    }
  }

  const getTypeColor = (type: string) => {
    return type === 'subscription' ? 'default' : 'secondary';
  };

  const getStatusColor = (status: string) => {
    if (status === 'active' || status === 'paid') return 'default';
    if (status === 'pending') return 'secondary';
    return 'destructive';
  };

  // Filter and Sort Logic
  const filteredAndSorted = useMemo(() => {
    let result = [...purchases];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.user_email.toLowerCase().includes(term) ||
        item.details.toLowerCase().includes(term) ||
        item.status.toLowerCase().includes(term)
      );
    }
    
    // Apply type filter
    if (typeFilter !== 'all') {
      result = result.filter(item => item.type === typeFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(item => item.status === statusFilter);
    }
    
    // Apply date range filter
    if (dateFrom) {
      result = result.filter(item => new Date(item.created_at) >= dateFrom);
    }
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      result = result.filter(item => new Date(item.created_at) <= endOfDay);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      // Handle null/undefined values with direction
      if (aVal === null || aVal === undefined) return direction;
      if (bVal === null || bVal === undefined) return -direction;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return 0;
    });
    
    return result;
  }, [purchases, searchTerm, typeFilter, statusFilter, dateFrom, dateTo, sortField, sortDirection]);

  const handleSort = (field: keyof Purchase) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const SortIcon = ({ field }: { field: keyof Purchase }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="ml-2 h-4 w-4" /> : 
      <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const activeFiltersCount = [
    searchTerm,
    typeFilter !== 'all',
    statusFilter !== 'all',
    dateFrom,
    dateTo
  ].filter(Boolean).length;

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({
        title: "Copied",
        description: "Payment ID copied to clipboard",
        duration: 2000,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="app-tile app-tile-hover">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="app-icon-box">
            <Coins className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="app-section-title">Credit Purchases</CardTitle>
            <CardDescription>
              All subscription activations and top-up purchases (all users)
            </CardDescription>
          </div>
        </div>
        
        {isTestMode && (
          <Alert className="mt-4 border-primary/40 bg-primary/5">
            <Clock className="h-4 w-4 text-primary" />
            <AlertDescription>
              <strong>🧪 Test Mode Active:</strong> All timestamps are relative to simulated time.
              <br />
              <span className="text-xs">
                Current simulated time: {format(getCurrentTime(), 'PPP HH:mm:ss')}
              </span>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Filter Controls */}
        <div className="flex flex-col gap-3 mt-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by User, Details, Status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={(value: any) => setTypeFilter(value)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Typ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="subscription">Subscription</SelectItem>
                <SelectItem value="topup">Top-up</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, 'dd.MM.yyyy') : 'From Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, 'dd.MM.yyyy') : 'To Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            {activeFiltersCount > 0 && (
              <Button variant="ghost" onClick={resetFilters}>
                <X className="mr-2 h-4 w-4" />
                Reset Filters ({activeFiltersCount})
              </Button>
            )}
          </div>
          
          {/* Result Count */}
          <p className="text-sm text-muted-foreground">
            {filteredAndSorted.length} of {totalCount} entries
            {filteredAndSorted.length !== totalCount && ' (filtered)'}
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{loadingMessage}</p>
          </div>
        ) : purchases.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No credit purchases found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Date/Time
                      <SortIcon field="created_at" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('user_email')}
                  >
                    <div className="flex items-center">
                      User
                      <SortIcon field="user_email" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center">
                      Type
                      <SortIcon field="type" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('details')}
                  >
                    <div className="flex items-center">
                      Details
                      <SortIcon field="details" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('credits_before')}
                  >
                    <div className="flex items-center justify-end whitespace-nowrap">
                      Before
                      <SortIcon field="credits_before" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('credits')}
                  >
                    <div className="flex items-center justify-end whitespace-nowrap">
                      Credits
                      <SortIcon field="credits" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('credits_after')}
                  >
                    <div className="flex items-center justify-end whitespace-nowrap">
                      After
                      <SortIcon field="credits_after" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('amount')}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      <SortIcon field="amount" />
                    </div>
                  </TableHead>
                  <TableHead>Payment ID</TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <SortIcon field="status" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell className={cn(
                      "whitespace-nowrap",
                      isTestMode && "text-primary font-medium"
                    )}>
                      {new Date(purchase.created_at).toLocaleString('de-DE')}
                      {isTestMode && <span className="text-xs ml-1">(sim)</span>}
                    </TableCell>
                    <TableCell className="font-medium">{purchase.user_email}</TableCell>
                    <TableCell>
                      <Badge variant={getTypeColor(purchase.type)}>
                        {purchase.type === 'subscription' ? 'Subscription' : 'Top-up'}
                      </Badge>
                    </TableCell>
                    <TableCell>{purchase.details}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {purchase.credits_before !== null && purchase.credits_before !== undefined 
                        ? purchase.credits_before.toLocaleString('de-DE') 
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right font-bold text-success">
                      +{purchase.credits.toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {purchase.credits_after !== null && purchase.credits_after !== undefined 
                        ? purchase.credits_after.toLocaleString('de-DE') 
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {purchase.amount ? `€${purchase.amount.toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell>
                      {purchase.stripe_payment_id && purchase.type === 'topup' ? (
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono text-muted-foreground">
                            {purchase.stripe_payment_id.substring(0, 15)}...
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(purchase.stripe_payment_id!, purchase.id)}
                          >
                            {copiedId === purchase.id ? (
                              <Check className="h-3 w-3 text-success" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(purchase.status)}>
                        {purchase.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
